import { Platform } from "react-native";
import type {
  CategoryTypeIdentifier,
  ObjectTypeIdentifier,
  QuantityTypeIdentifier,
} from "@kingstinct/react-native-healthkit";
import type { ApiClient } from "@src/packages/api/louise-api";
import {
  categoryObservation,
  quantityObservation,
  type CategoryConfig,
  type HealthObservationInput,
  type QuantityConfig,
} from "@src/packages/health/healthObservationPayload";
import type { paths } from "@src/packages/openapi/schema";
import { getDeviceTimezone, toLocalDateString } from "@src/packages/utils/date";

type HealthKitModule = typeof import("@kingstinct/react-native-healthkit");
type CategorySample = Awaited<
  ReturnType<HealthKitModule["queryCategorySamples"]>
>[number];
type QuantitySample = Awaited<
  ReturnType<HealthKitModule["queryQuantitySamples"]>
>[number];
type PeriodLogBody = NonNullable<
  paths["/patients/{patientId}/periods"]["post"]["requestBody"]
>["content"]["application/json"];

const MENSTRUAL_FLOW_TYPE = "HKCategoryTypeIdentifierMenstrualFlow";
const DEFAULT_LOOKBACK_DAYS = 365;
const MAX_QUANTITY_SAMPLES_PER_TYPE = 750;
const MAX_CATEGORY_SAMPLES_PER_TYPE = 1000;
const HEALTH_OBSERVATION_BATCH_SIZE = 250;

const CATEGORY_TYPES: CategoryConfig[] = [
  {
    identifier: MENSTRUAL_FLOW_TYPE,
    category: "reproductive",
    label: "Menstrual flow",
  },
  {
    identifier: "HKCategoryTypeIdentifierCervicalMucusQuality",
    category: "reproductive",
    label: "Cervical mucus quality",
  },
  {
    identifier: "HKCategoryTypeIdentifierOvulationTestResult",
    category: "reproductive",
    label: "Ovulation test result",
  },
  {
    identifier: "HKCategoryTypeIdentifierProgesteroneTestResult",
    category: "reproductive",
    label: "Progesterone test result",
  },
  {
    identifier: "HKCategoryTypeIdentifierPregnancyTestResult",
    category: "reproductive",
    label: "Pregnancy test result",
  },
  {
    identifier: "HKCategoryTypeIdentifierSexualActivity",
    category: "reproductive",
    label: "Sexual activity",
  },
  {
    identifier: "HKCategoryTypeIdentifierContraceptive",
    category: "reproductive",
    label: "Contraceptive",
  },
  {
    identifier: "HKCategoryTypeIdentifierIntermenstrualBleeding",
    category: "reproductive",
    label: "Intermenstrual bleeding",
  },
  {
    identifier: "HKCategoryTypeIdentifierPersistentIntermenstrualBleeding",
    category: "reproductive",
    label: "Persistent intermenstrual bleeding",
  },
  {
    identifier: "HKCategoryTypeIdentifierIrregularMenstrualCycles",
    category: "reproductive",
    label: "Irregular menstrual cycles",
  },
  {
    identifier: "HKCategoryTypeIdentifierInfrequentMenstrualCycles",
    category: "reproductive",
    label: "Infrequent menstrual cycles",
  },
  {
    identifier: "HKCategoryTypeIdentifierProlongedMenstrualPeriods",
    category: "reproductive",
    label: "Prolonged menstrual periods",
  },
  {
    identifier: "HKCategoryTypeIdentifierPregnancy",
    category: "reproductive",
    label: "Pregnancy",
  },
  {
    identifier: "HKCategoryTypeIdentifierLactation",
    category: "reproductive",
    label: "Lactation",
  },
  {
    identifier: "HKCategoryTypeIdentifierBleedingDuringPregnancy",
    category: "reproductive",
    label: "Bleeding during pregnancy",
  },
  {
    identifier: "HKCategoryTypeIdentifierBleedingAfterPregnancy",
    category: "reproductive",
    label: "Bleeding after pregnancy",
  },
  {
    identifier: "HKCategoryTypeIdentifierAbdominalCramps",
    category: "symptom",
    label: "Abdominal cramps",
  },
  {
    identifier: "HKCategoryTypeIdentifierPelvicPain",
    category: "symptom",
    label: "Pelvic pain",
  },
  {
    identifier: "HKCategoryTypeIdentifierBloating",
    category: "symptom",
    label: "Bloating",
  },
  {
    identifier: "HKCategoryTypeIdentifierBreastPain",
    category: "symptom",
    label: "Breast pain",
  },
  {
    identifier: "HKCategoryTypeIdentifierAcne",
    category: "symptom",
    label: "Acne",
  },
  {
    identifier: "HKCategoryTypeIdentifierMoodChanges",
    category: "symptom",
    label: "Mood changes",
  },
  {
    identifier: "HKCategoryTypeIdentifierNausea",
    category: "symptom",
    label: "Nausea",
  },
  {
    identifier: "HKCategoryTypeIdentifierFatigue",
    category: "symptom",
    label: "Fatigue",
  },
  {
    identifier: "HKCategoryTypeIdentifierHeadache",
    category: "symptom",
    label: "Headache",
  },
  {
    identifier: "HKCategoryTypeIdentifierHotFlashes",
    category: "symptom",
    label: "Hot flashes",
  },
  {
    identifier: "HKCategoryTypeIdentifierVaginalDryness",
    category: "symptom",
    label: "Vaginal dryness",
  },
  {
    identifier: "HKCategoryTypeIdentifierLowerBackPain",
    category: "symptom",
    label: "Lower back pain",
  },
  {
    identifier: "HKCategoryTypeIdentifierDizziness",
    category: "symptom",
    label: "Dizziness",
  },
  {
    identifier: "HKCategoryTypeIdentifierAppetiteChanges",
    category: "symptom",
    label: "Appetite changes",
  },
  {
    identifier: "HKCategoryTypeIdentifierSleepChanges",
    category: "symptom",
    label: "Sleep changes",
  },
  {
    identifier: "HKCategoryTypeIdentifierNightSweats",
    category: "symptom",
    label: "Night sweats",
  },
  {
    identifier: "HKCategoryTypeIdentifierFever",
    category: "symptom",
    label: "Fever",
  },
  {
    identifier: "HKCategoryTypeIdentifierDiarrhea",
    category: "symptom",
    label: "Diarrhea",
  },
  {
    identifier: "HKCategoryTypeIdentifierConstipation",
    category: "symptom",
    label: "Constipation",
  },
  {
    identifier: "HKCategoryTypeIdentifierVomiting",
    category: "symptom",
    label: "Vomiting",
  },
  {
    identifier: "HKCategoryTypeIdentifierSleepAnalysis",
    category: "sleep",
    label: "Sleep analysis",
  },
  {
    identifier: "HKCategoryTypeIdentifierSleepApneaEvent",
    category: "sleep",
    label: "Sleep apnea event",
  },
  {
    identifier: "HKCategoryTypeIdentifierLowHeartRateEvent",
    category: "vital",
    label: "Low heart rate event",
  },
  {
    identifier: "HKCategoryTypeIdentifierHighHeartRateEvent",
    category: "vital",
    label: "High heart rate event",
  },
  {
    identifier: "HKCategoryTypeIdentifierIrregularHeartRhythmEvent",
    category: "vital",
    label: "Irregular heart rhythm event",
  },
];

const QUANTITY_TYPES: QuantityConfig[] = [
  {
    identifier: "HKQuantityTypeIdentifierBasalBodyTemperature",
    category: "reproductive",
    label: "Basal body temperature",
    unit: "degC",
  },
  {
    identifier: "HKQuantityTypeIdentifierAppleSleepingWristTemperature",
    category: "vital",
    label: "Sleeping wrist temperature",
    unit: "degC",
  },
  {
    identifier: "HKQuantityTypeIdentifierBodyTemperature",
    category: "vital",
    label: "Body temperature",
    unit: "degC",
  },
  {
    identifier: "HKQuantityTypeIdentifierHeartRate",
    category: "vital",
    label: "Heart rate",
    unit: "count/min",
    limit: 750,
  },
  {
    identifier: "HKQuantityTypeIdentifierRestingHeartRate",
    category: "vital",
    label: "Resting heart rate",
    unit: "count/min",
  },
  {
    identifier: "HKQuantityTypeIdentifierHeartRateVariabilitySDNN",
    category: "vital",
    label: "Heart rate variability SDNN",
    unit: "ms",
  },
  {
    identifier: "HKQuantityTypeIdentifierRespiratoryRate",
    category: "vital",
    label: "Respiratory rate",
    unit: "count/min",
  },
  {
    identifier: "HKQuantityTypeIdentifierOxygenSaturation",
    category: "vital",
    label: "Oxygen saturation",
    unit: "%",
  },
  {
    identifier: "HKQuantityTypeIdentifierBloodGlucose",
    category: "vital",
    label: "Blood glucose",
    unit: "mg/dL",
  },
  {
    identifier: "HKQuantityTypeIdentifierBloodPressureSystolic",
    category: "vital",
    label: "Blood pressure systolic",
    unit: "mmHg",
  },
  {
    identifier: "HKQuantityTypeIdentifierBloodPressureDiastolic",
    category: "vital",
    label: "Blood pressure diastolic",
    unit: "mmHg",
  },
  {
    identifier: "HKQuantityTypeIdentifierStepCount",
    category: "activity",
    label: "Steps",
    unit: "count",
  },
  {
    identifier: "HKQuantityTypeIdentifierActiveEnergyBurned",
    category: "activity",
    label: "Active energy burned",
    unit: "kcal",
  },
  {
    identifier: "HKQuantityTypeIdentifierAppleExerciseTime",
    category: "activity",
    label: "Exercise time",
    unit: "min",
  },
  {
    identifier: "HKQuantityTypeIdentifierDistanceWalkingRunning",
    category: "activity",
    label: "Walking and running distance",
    unit: "m",
  },
  {
    identifier: "HKQuantityTypeIdentifierFlightsClimbed",
    category: "activity",
    label: "Flights climbed",
    unit: "count",
  },
  {
    identifier: "HKQuantityTypeIdentifierVO2Max",
    category: "activity",
    label: "VO2 max",
    unit: "mL/kg*min",
  },
  {
    identifier: "HKQuantityTypeIdentifierBodyMass",
    category: "body",
    label: "Body mass",
    unit: "kg",
  },
  {
    identifier: "HKQuantityTypeIdentifierBodyMassIndex",
    category: "body",
    label: "BMI",
    unit: "count",
  },
  {
    identifier: "HKQuantityTypeIdentifierBodyFatPercentage",
    category: "body",
    label: "Body fat percentage",
    unit: "%",
  },
  {
    identifier: "HKQuantityTypeIdentifierHeight",
    category: "body",
    label: "Height",
    unit: "m",
  },
  {
    identifier: "HKQuantityTypeIdentifierWaistCircumference",
    category: "body",
    label: "Waist circumference",
    unit: "m",
  },
  {
    identifier: "HKQuantityTypeIdentifierDietaryCaffeine",
    category: "nutrition",
    label: "Caffeine",
    unit: "mg",
  },
  {
    identifier: "HKQuantityTypeIdentifierDietaryWater",
    category: "nutrition",
    label: "Water",
    unit: "L",
  },
  {
    identifier: "HKQuantityTypeIdentifierDietaryEnergyConsumed",
    category: "nutrition",
    label: "Energy consumed",
    unit: "kcal",
  },
  {
    identifier: "HKQuantityTypeIdentifierNumberOfAlcoholicBeverages",
    category: "nutrition",
    label: "Alcoholic beverages",
    unit: "count",
  },
];

export type AppleHealthPeriodFlow = PeriodLogBody["flow"];

export type AppleHealthPeriodDay = {
  date: string;
  flow: AppleHealthPeriodFlow;
  notes: string | null;
  sourceIds: string[];
};

export type AppleHealthPeriodSyncResult = {
  imported: number;
  skipped: number;
  failed: number;
  totalSamples: number;
  days: AppleHealthPeriodDay[];
  observationsImported: number;
  observationsFailed: number;
  observationsRead: number;
};

const FLOW_RANK: Record<AppleHealthPeriodFlow, number> = {
  spotting: 0,
  light: 1,
  medium: 2,
  heavy: 3,
};

function lookbackStart(days: number): Date {
  const date = new Date();
  date.setDate(date.getDate() - days);
  date.setHours(0, 0, 0, 0);
  return date;
}

async function loadHealthKit(): Promise<HealthKitModule> {
  if (Platform.OS !== "ios") {
    throw new Error("Apple Health is only available on iOS.");
  }
  return import("@kingstinct/react-native-healthkit");
}

function uniqueIdentifiers(
  identifiers: readonly ObjectTypeIdentifier[],
): ObjectTypeIdentifier[] {
  return [...new Set(identifiers)];
}

function availableIdentifiers(
  healthkit: HealthKitModule,
  identifiers: readonly ObjectTypeIdentifier[],
): ObjectTypeIdentifier[] {
  return uniqueIdentifiers(identifiers).filter((identifier) => {
    try {
      return healthkit.isObjectTypeAvailable(identifier);
    } catch {
      return false;
    }
  });
}

function normalizeFlow(
  value: CategorySample["value"],
  healthkit: HealthKitModule,
): AppleHealthPeriodFlow | null {
  switch (value) {
    case healthkit.CategoryValueMenstrualFlow.light:
      return "light";
    case healthkit.CategoryValueMenstrualFlow.medium:
      return "medium";
    case healthkit.CategoryValueMenstrualFlow.heavy:
      return "heavy";
    case healthkit.CategoryValueMenstrualFlow.unspecified:
      return "medium";
    case healthkit.CategoryValueMenstrualFlow.none:
      return null;
    default:
      return null;
  }
}

function sourceIdFor(sample: CategorySample | QuantitySample): string {
  return sample.sourceRevision?.source?.bundleIdentifier ?? sample.uuid;
}

async function readCategoryObservations(
  healthkit: HealthKitModule,
  lookbackDays: number,
): Promise<HealthObservationInput[]> {
  const queryCategorySamples = healthkit.queryCategorySamples as (
    identifier: CategoryTypeIdentifier,
    options: {
      limit: number;
      ascending: boolean;
      filter: { date: { startDate: Date; endDate: Date } };
    },
  ) => Promise<readonly CategorySample[]>;
  const observations: HealthObservationInput[] = [];
  const startDate = lookbackStart(lookbackDays);
  const endDate = new Date();
  const deviceTimeZone = getDeviceTimezone();

  for (const config of CATEGORY_TYPES) {
    if (!healthkit.isObjectTypeAvailable(config.identifier)) continue;
    try {
      const samples = await queryCategorySamples(config.identifier, {
        limit: MAX_CATEGORY_SAMPLES_PER_TYPE,
        ascending: true,
        filter: { date: { startDate, endDate } },
      });
      observations.push(
        ...samples.map((sample) =>
          categoryObservation(sample, config, healthkit, deviceTimeZone),
        ),
      );
    } catch (error) {
      console.warn("[AppleHealth] Failed to read category samples", {
        identifier: config.identifier,
        error,
      });
    }
  }

  return observations;
}

async function readQuantityObservations(
  healthkit: HealthKitModule,
  lookbackDays: number,
): Promise<HealthObservationInput[]> {
  const queryQuantitySamples = healthkit.queryQuantitySamples as (
    identifier: QuantityTypeIdentifier,
    options: {
      limit: number;
      ascending: boolean;
      unit: string;
      filter: { date: { startDate: Date; endDate: Date } };
    },
  ) => Promise<readonly QuantitySample[]>;
  const observations: HealthObservationInput[] = [];
  const startDate = lookbackStart(lookbackDays);
  const endDate = new Date();
  const deviceTimeZone = getDeviceTimezone();

  for (const config of QUANTITY_TYPES) {
    if (!healthkit.isObjectTypeAvailable(config.identifier)) continue;
    try {
      const samples = await queryQuantitySamples(config.identifier, {
        limit: config.limit ?? MAX_QUANTITY_SAMPLES_PER_TYPE,
        ascending: true,
        unit: config.unit,
        filter: { date: { startDate, endDate } },
      });
      observations.push(
        ...samples.map((sample) =>
          quantityObservation(sample, config, deviceTimeZone),
        ),
      );
    } catch (error) {
      console.warn("[AppleHealth] Failed to read quantity samples", {
        identifier: config.identifier,
        error,
      });
    }
  }

  return observations;
}

export async function requestAppleHealthAuthorization(): Promise<void> {
  const healthkit = await loadHealthKit();

  if (!healthkit.isHealthDataAvailable()) {
    throw new Error("Apple Health data is not available on this device.");
  }

  const toRead = availableIdentifiers(healthkit, [
    ...CATEGORY_TYPES.map((config) => config.identifier),
    ...QUANTITY_TYPES.map((config) => config.identifier),
  ]);

  if (toRead.length === 0) {
    throw new Error("No supported Apple Health data types are available.");
  }

  const authorized = await healthkit.requestAuthorization({ toRead });

  if (!authorized) {
    throw new Error("Apple Health permission was not granted.");
  }
}

export async function requestAppleHealthPeriodAuthorization(): Promise<void> {
  await requestAppleHealthAuthorization();
}

export async function readAppleHealthPeriodDays(
  lookbackDays = DEFAULT_LOOKBACK_DAYS,
): Promise<{
  totalSamples: number;
  days: AppleHealthPeriodDay[];
}> {
  const healthkit = await loadHealthKit();
  const queryCategorySamples = healthkit.queryCategorySamples as (
    identifier: CategoryTypeIdentifier,
    options: {
      limit: number;
      ascending: boolean;
      filter: { date: { startDate: Date; endDate: Date } };
    },
  ) => Promise<readonly CategorySample[]>;

  const samples = await queryCategorySamples(MENSTRUAL_FLOW_TYPE, {
    limit: 0,
    ascending: true,
    filter: {
      date: {
        startDate: lookbackStart(lookbackDays),
        endDate: new Date(),
      },
    },
  });

  const daysByDate = new Map<string, AppleHealthPeriodDay>();

  for (const sample of samples) {
    const flow = normalizeFlow(sample.value, healthkit);
    if (!flow) continue;

    const date = toLocalDateString(sample.startDate);
    const existing = daysByDate.get(date);
    const nextDay: AppleHealthPeriodDay = {
      date,
      flow,
      notes:
        sample.value === healthkit.CategoryValueMenstrualFlow.unspecified
          ? "Imported from Apple Health. Flow was unspecified."
          : "Imported from Apple Health.",
      sourceIds: [sourceIdFor(sample)],
    };

    if (!existing) {
      daysByDate.set(date, nextDay);
      continue;
    }

    existing.sourceIds.push(sourceIdFor(sample));
    if (FLOW_RANK[flow] > FLOW_RANK[existing.flow]) {
      existing.flow = flow;
      existing.notes = nextDay.notes;
    }
  }

  return {
    totalSamples: samples.length,
    days: [...daysByDate.values()],
  };
}

export async function readAppleHealthObservations(
  lookbackDays = DEFAULT_LOOKBACK_DAYS,
): Promise<HealthObservationInput[]> {
  const healthkit = await loadHealthKit();
  const [categoryObservations, quantityObservations] = await Promise.all([
    readCategoryObservations(healthkit, lookbackDays),
    readQuantityObservations(healthkit, lookbackDays),
  ]);
  return [...categoryObservations, ...quantityObservations];
}

async function postHealthObservations({
  client,
  patientId,
  observations,
}: {
  client: ApiClient;
  patientId: string;
  observations: HealthObservationInput[];
}): Promise<{ imported: number; failed: number }> {
  let imported = 0;
  let failed = 0;

  for (let i = 0; i < observations.length; i += HEALTH_OBSERVATION_BATCH_SIZE) {
    const batch = observations.slice(i, i + HEALTH_OBSERVATION_BATCH_SIZE);
    const response = await client.POST(
      "/patients/{patientId}/health-observations",
      {
        params: { path: { patientId } },
        body: { observations: batch },
      },
    );

    if (response.error) {
      failed += batch.length;
      continue;
    }
    imported += response.data?.data.length ?? batch.length;
  }

  return { imported, failed };
}

export async function syncAppleHealthPeriods({
  client,
  patientId,
  lookbackDays = DEFAULT_LOOKBACK_DAYS,
}: {
  client: ApiClient;
  patientId: string;
  lookbackDays?: number;
}): Promise<AppleHealthPeriodSyncResult> {
  await requestAppleHealthAuthorization();

  const [{ totalSamples, days }, observations] = await Promise.all([
    readAppleHealthPeriodDays(lookbackDays),
    readAppleHealthObservations(lookbackDays),
  ]);
  let failed = 0;

  for (const day of days) {
    const response = await client.POST("/patients/{patientId}/periods", {
      params: { path: { patientId } },
      body: {
        date: day.date,
        flow: day.flow,
        notes: day.notes,
      },
    });

    if (response.error) {
      failed += 1;
    }
  }

  const observationResult = await postHealthObservations({
    client,
    patientId,
    observations,
  });

  return {
    imported: days.length - failed,
    skipped: 0,
    failed,
    totalSamples,
    days,
    observationsImported: observationResult.imported,
    observationsFailed: observationResult.failed,
    observationsRead: observations.length,
  };
}
